/**
 * The question, and the control that answers it.
 *
 * An analog knob rather than a pair of tabs, because the whole page is one
 * comparison and the reader should feel they are turning a dial on a single
 * question rather than navigating between two pages. The detent positions are
 * Yes and No; there is no third setting and no in-between.
 *
 * It is a `role="switch"` button underneath, so it is operable by keyboard and
 * announced correctly. The dial is decoration over a real control, never a
 * substitute for one.
 */

import { esc } from './num.js';

export interface KnobOpts {
  question: string;
  yesId: string;
  noId: string;
  yesLabel: string;
  noLabel: string;
  /** Which scenario is currently selected. */
  current: string;
}

/** Knurl marks around the bezel. Decorative, so they carry aria-hidden. */
function knurls(): string {
  const marks: string[] = [];
  for (let i = 0; i < 36; i++) {
    const a = (i / 36) * Math.PI * 2;
    const x1 = 60 + Math.cos(a) * 47;
    const y1 = 60 + Math.sin(a) * 47;
    const x2 = 60 + Math.cos(a) * 52;
    const y2 = 60 + Math.sin(a) * 52;
    marks.push(
      `<line x1="${x1.toFixed(2)}" y1="${y1.toFixed(2)}" x2="${x2.toFixed(
        2,
      )}" y2="${y2.toFixed(2)}" stroke="currentColor" stroke-width="1.4" opacity="0.42" />`,
    );
  }
  return marks.join('');
}

export function knob(opts: KnobOpts): string {
  const isYes = opts.current === opts.yesId;
  const target = isYes ? opts.noId : opts.yesId;

  return `<section class="knob-block"><div class="container">
    <h2 class="knob-question">${esc(opts.question)}</h2>

    <div class="knob-row">
      <span class="knob-side ${isYes ? 'on' : ''}" aria-hidden="true">${esc(opts.yesLabel)}</span>

      <button
        class="knob ${isYes ? 'is-yes' : 'is-no'}"
        role="switch"
        aria-checked="${isYes}"
        aria-label="${esc(opts.question)}"
        data-scenario="${esc(target)}"
      >
        <svg viewBox="0 0 120 120" width="120" height="120" aria-hidden="true">
          <defs>
            <radialGradient id="knobFace" cx="38%" cy="30%" r="78%">
              <stop offset="0%" stop-color="var(--bg-card)" />
              <stop offset="62%" stop-color="var(--bg-card-2)" />
              <stop offset="100%" stop-color="var(--rule)" />
            </radialGradient>
          </defs>

          <g class="knob-knurls" color="var(--ink-faint)">${knurls()}</g>

          <circle cx="60" cy="60" r="46" fill="var(--rule-soft)" />
          <circle cx="60" cy="60" r="43" fill="url(#knobFace)" stroke="var(--ink)" stroke-width="1.5" />

          <g class="knob-dial">
            <line x1="60" y1="60" x2="60" y2="24" stroke="var(--ink)" stroke-width="4" stroke-linecap="round" />
            <circle cx="60" cy="60" r="6" fill="var(--ink)" />
          </g>
        </svg>
      </button>

      <span class="knob-side ${isYes ? '' : 'on'}" aria-hidden="true">${esc(opts.noLabel)}</span>
    </div>

  </div></section>`;
}
